/** Active-outage list (spec §8): sortable / filterable, CURSOR-paginated table
 *  with per-row TRUST signals — source/confidence, confirmed-vs-inferred badge,
 *  last-signal, duration, customers + critical-count badge. The critical count
 *  renders "Protected" (never a number) when the server suppressed it.
 *
 *  Grouped (parent-first) view: the default query returns only TOP-LEVEL events
 *  (roots_only) so a major outage's ~270 flow-through events don't bury the one
 *  row that matters. Each parent row shows a child summary and a chevron that
 *  drills down into its child events on demand (2026-07-21 d911 finding).
 *
 *  Cursor pagination: we keep a stack of cursors so the operator can page
 *  forward and back. Filters/sort reset the stack (a new query). */

import { Fragment, useEffect, useMemo, useState } from 'react'
import { useOutages } from '../../hooks/useOms.ts'
import type { OutageListItem, OutageScope, OutageStatus } from '../../types/oms.ts'
import type { OutageSort } from '../../api/oms.ts'
import { friendlyError } from '../../utils/friendlyError.ts'
import { basisBadge } from './dispatchFormat.ts'
import {
  CriticalCountBadge,
  ScopeBadge,
  SourceBadge,
  StatusBadge,
  TrustBadge,
  deviceLabel,
  formatAgo,
  formatDateTime,
  formatDuration,
} from './omsFormat.tsx'

const PAGE_SIZE = 25

const SCOPE_OPTIONS: OutageScope[] = ['meter', 'transformer', 'device', 'feeder', 'substation']
const STATUS_OPTIONS: OutageStatus[] = ['active', 'restored', 'closed', 'cancelled']

const SORT_OPTIONS: { value: OutageSort; label: string }[] = [
  { value: 'severity', label: 'Severity (scope + customers)' },
  { value: 'start_time', label: 'Start time' },
  { value: 'customers_affected', label: 'Customers affected' },
  { value: 'status', label: 'Status' },
  { value: 'updated_at', label: 'Last updated' },
]

/** "177 transformers · 3 meters" from child_counts, highest scope first. */
function childSummary(counts: Record<string, number> | null): string | null {
  if (!counts) return null
  const order = ['substation', 'feeder', 'device', 'transformer', 'meter']
  const parts = order
    .filter(s => counts[s])
    .map(s => `${counts[s]} ${s}${counts[s]! > 1 ? 's' : ''}`)
  return parts.length ? parts.join(' · ') : null
}

function OutageRow({
  item, selectedId, onSelect, onLocate, indent = false,
  expandable = false, isExpanded = false, onToggleExpand,
}: {
  item: OutageListItem
  selectedId: number | null
  onSelect: (item: OutageListItem) => void
  onLocate?: (id: number) => void
  indent?: boolean
  expandable?: boolean
  isExpanded?: boolean
  onToggleExpand?: () => void
}) {
  const summary = childSummary(item.child_counts)
  return (
    <tr
      onClick={() => onSelect(item)}
      className={`border-b border-gray-100 cursor-pointer hover:bg-blue-50 ${
        selectedId === item.id ? 'bg-blue-50' : ''
      } ${indent ? 'bg-gray-50/50' : ''}`}
    >
      <td className={`p-2 font-mono text-xs whitespace-nowrap ${indent ? 'pl-7' : ''}`}>
        <span className="inline-flex items-center gap-1">
          {expandable && (
            <button
              type="button"
              aria-label={`Show events under #${item.id}`}
              aria-expanded={isExpanded}
              onClick={e => {
                e.stopPropagation()
                onToggleExpand?.()
              }}
              className="w-4 text-gray-500 hover:text-gray-800"
            >
              {isExpanded ? '▾' : '▸'}
            </button>
          )}
          #{item.id}
        </span>
        <div className="text-[10px] text-gray-400">
          {deviceLabel(
            item.scope,
            item.device_id,
            item.unresolved_meter_number,
            item.suggestion_detail,
          )}
        </div>
        {summary && (
          <div className="text-[10px] text-blue-700 font-sans">{summary}</div>
        )}
      </td>
      <td className="p-2 whitespace-nowrap">
        <ScopeBadge scope={item.scope} />
      </td>
      <td className="p-2">
        <StatusBadge status={item.status} />
      </td>
      <td className="p-2">
        <TrustBadge item={item} />
      </td>
      <td className="p-2 whitespace-nowrap">
        <SourceBadge item={item} />
      </td>
      <td className="p-2 text-xs whitespace-nowrap">
        {(() => {
          const loc = item.dispatch_location ?? null
          const badge = basisBadge(loc)
          return (
            <div className="flex items-center gap-1.5">
              <div className="min-w-0">
                {loc?.device_label && (
                  <div className="text-gray-700 truncate max-w-[140px]" title={loc.device_label}>
                    {loc.device_label}
                  </div>
                )}
                <span
                  title={badge.title}
                  className="inline-block px-1 rounded bg-gray-100 text-[10px] text-gray-600"
                >
                  {badge.text}
                </span>
              </div>
              {onLocate && loc && (
                <button
                  type="button"
                  aria-label={`Locate #${item.id} on map`}
                  title="Locate on map"
                  onClick={e => {
                    e.stopPropagation()
                    onLocate(item.id)
                  }}
                  className="text-gray-500 hover:text-blue-700 text-sm leading-none"
                >
                  ⌖
                </button>
              )}
            </div>
          )
        })()}
      </td>
      <td className="p-2 text-xs text-gray-600 whitespace-nowrap">
        {formatAgo(item.last_signal_time)}
      </td>
      <td className="p-2 text-xs text-gray-600 whitespace-nowrap">
        {formatDuration(item.duration_seconds)}
      </td>
      <td
        className="p-2 text-xs text-gray-600 whitespace-nowrap"
        title="Estimated restoration (operator-set)"
      >
        {item.estimated_restoration_at
          ? formatDateTime(item.estimated_restoration_at)
          : '—'}
      </td>
      <td className="p-2 text-right text-xs">
        {item.customers_affected ?? '--'}
      </td>
      <td className="p-2">
        <CriticalCountBadge
          count={item.critical_customers_affected}
          protectedFlag={item.critical_customers_protected}
        />
      </td>
    </tr>
  )
}

/** Children of an expanded parent, fetched on demand. limit grows in place of
 *  cursor-walking (server caps at 200 — covers d911's 177; if more exist we say
 *  so instead of silently truncating). */
function ChildOutageRows({
  parentId, selectedId, onSelect, onLocate,
}: {
  parentId: number
  selectedId: number | null
  onSelect: (item: OutageListItem) => void
  onLocate?: (id: number) => void
}) {
  const [limit, setLimit] = useState(25)
  const { data, isLoading } = useOutages({
    parent_id: parentId, sort: 'severity', limit,
  })
  const items = data?.items ?? []
  const hasMore = data?.next_cursor != null
  return (
    <>
      {isLoading && (
        <tr>
          <td colSpan={11} className="p-2 pl-7 text-xs text-gray-400">
            Loading events under #{parentId}…
          </td>
        </tr>
      )}
      {items.map(child => (
        <OutageRow
          key={child.id}
          item={child}
          selectedId={selectedId}
          onSelect={onSelect}
          onLocate={onLocate}
          indent
        />
      ))}
      {hasMore && (
        <tr>
          <td colSpan={11} className="p-2 pl-7">
            {limit < 200 ? (
              <button
                onClick={() => setLimit(l => Math.min(200, l + 50))}
                className="text-xs text-blue-600 hover:underline"
              >
                Show more events under #{parentId}…
              </button>
            ) : (
              <span className="text-xs text-gray-400">
                Showing the first 200 events under #{parentId}.
              </span>
            )}
          </td>
        </tr>
      )}
    </>
  )
}

export function ActiveOutageList({
  selectedId,
  onSelect,
  onLocate,
}: {
  selectedId: number | null
  onSelect: (item: OutageListItem) => void
  onLocate?: (id: number) => void
}) {
  const [statusFilter, setStatusFilter] = useState<OutageStatus | ''>('active')
  const [scopeFilter, setScopeFilter] = useState<OutageScope | ''>('')
  const [sort, setSort] = useState<OutageSort>('severity')
  // Cursor stack: cursorStack[i] is the cursor that loaded page i (index 0 =
  // null = first page). `page` indexes into it.
  const [cursorStack, setCursorStack] = useState<(string | null)[]>([null])
  const [page, setPage] = useState(0)
  const [expanded, setExpanded] = useState<Set<number>>(new Set())

  // Grouped (parent-first) whenever the view is active + all scopes. A scope or
  // status filter is a flat query — grouping would hide matches. Changing the
  // SORT does NOT disable grouping: root pages re-order but stay grouped.
  const grouped = statusFilter === 'active' && scopeFilter === ''

  // Reset pagination whenever the query shape changes.
  useEffect(() => {
    setCursorStack([null])
    setPage(0)
    setExpanded(new Set())
  }, [statusFilter, scopeFilter, sort, grouped])

  const params = useMemo(
    () => ({
      status: statusFilter || undefined,
      scope: scopeFilter || undefined,
      sort,
      limit: PAGE_SIZE,
      cursor: cursorStack[page] ?? undefined,
      roots_only: grouped ? true : undefined,
    }),
    [statusFilter, scopeFilter, sort, cursorStack, page, grouped],
  )

  const { data, isLoading, isError, error, isFetching } = useOutages(params)

  const items = data?.items ?? []
  const hasNext = data?.next_cursor != null
  const hasPrev = page > 0

  function goNext() {
    if (!hasNext || data?.next_cursor == null) return
    setCursorStack(prev => {
      const next = prev.slice(0, page + 1)
      next.push(data.next_cursor)
      return next
    })
    setPage(p => p + 1)
  }

  function goPrev() {
    if (!hasPrev) return
    setPage(p => Math.max(0, p - 1))
  }

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 flex flex-col min-h-0">
      {/* Filter bar */}
      <div className="flex flex-wrap items-center gap-2 px-3 py-2 border-b border-gray-200">
        <select
          value={statusFilter}
          onChange={e => setStatusFilter(e.target.value as OutageStatus | '')}
          className="text-xs border border-gray-300 rounded-md px-2 py-1 bg-white"
        >
          <option value="">All statuses</option>
          {STATUS_OPTIONS.map(s => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
        <select
          value={scopeFilter}
          onChange={e => setScopeFilter(e.target.value as OutageScope | '')}
          className="text-xs border border-gray-300 rounded-md px-2 py-1 bg-white"
        >
          <option value="">All scopes</option>
          {SCOPE_OPTIONS.map(s => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
        <div className="flex items-center gap-1">
          <label className="text-xs text-gray-500">Sort:</label>
          <select
            value={sort}
            onChange={e => setSort(e.target.value as OutageSort)}
            className="text-xs border border-gray-300 rounded-md px-2 py-1 bg-white"
          >
            {SORT_OPTIONS.map(o => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </div>
        {isFetching && <span className="text-xs text-gray-400">Refreshing…</span>}
      </div>

      {/* Table */}
      <div className="overflow-auto">
        <table className="w-full text-sm">
          <thead className="sticky top-0 bg-gray-50">
            <tr className="text-left text-xs text-gray-500 uppercase tracking-wide border-b border-gray-200">
              <th className="p-2">Outage</th>
              <th className="p-2">Scope</th>
              <th className="p-2">Status</th>
              <th className="p-2">Trust</th>
              <th className="p-2">Source</th>
              <th className="p-2">Dispatch</th>
              <th className="p-2">Last signal</th>
              <th className="p-2">Duration</th>
              <th className="p-2" title="Estimated restoration (operator-set)">
                ETR
              </th>
              <th className="p-2 text-right">Customers</th>
              <th className="p-2">Critical</th>
            </tr>
          </thead>
          <tbody>
            {isLoading && (
              <tr>
                <td colSpan={11} className="p-6 text-center text-gray-400">
                  Loading outages…
                </td>
              </tr>
            )}
            {isError && (
              <tr>
                <td colSpan={11} className="p-6 text-center text-red-600">
                  {friendlyError(error, 'load outages')}
                </td>
              </tr>
            )}
            {!isLoading && !isError && items.length === 0 && (
              <tr>
                <td colSpan={11} className="p-6 text-center text-gray-400">
                  No outages match the current filters.
                </td>
              </tr>
            )}
            {items.map(item => {
              const expandable =
                grouped && item.child_counts != null &&
                Object.keys(item.child_counts).length > 0
              const isExpanded = expanded.has(item.id)
              return (
                <Fragment key={item.id}>
                  <OutageRow
                    item={item}
                    selectedId={selectedId}
                    onSelect={onSelect}
                    onLocate={onLocate}
                    expandable={expandable}
                    isExpanded={isExpanded}
                    onToggleExpand={() =>
                      setExpanded(prev => {
                        const next = new Set(prev)
                        if (next.has(item.id)) next.delete(item.id)
                        else next.add(item.id)
                        return next
                      })
                    }
                  />
                  {expandable && isExpanded && (
                    <ChildOutageRows
                      parentId={item.id}
                      selectedId={selectedId}
                      onSelect={onSelect}
                      onLocate={onLocate}
                    />
                  )}
                </Fragment>
              )
            })}
          </tbody>
        </table>
      </div>

      {/* Cursor pagination */}
      <div className="flex items-center justify-between px-3 py-2 border-t border-gray-100">
        <button
          onClick={goPrev}
          disabled={!hasPrev}
          className="text-sm px-3 py-1 rounded border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Previous
        </button>
        <span className="text-xs text-gray-500">
          Page {page + 1}
          {items.length > 0 ? ` · ${items.length} shown` : ''}
        </span>
        <button
          onClick={goNext}
          disabled={!hasNext}
          className="text-sm px-3 py-1 rounded border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Next
        </button>
      </div>
    </div>
  )
}
